import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTie, faEnvelope, faPhone, faCalendarAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import '../styles/dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(d);
  }
};

const formatTime = (t) => {
  if (!t) return '';
  const s = String(t);
  return s.length >= 5 ? s.slice(0, 5) : s;
};

export default function MyCounselors() {
  const { token } = useAuth();
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/portal/counselors`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (data.success) setCounselors(data.counselors || []);
        else setError(data.message || 'Failed to load counselors');
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="dashboard-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Assigned Counselors</h1>
              <p className="page-subtitle">Counselors assigned to you and your scheduled meetings</p>
            </div>
          </div>

          {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}
          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '14px 16px', borderRadius: '10px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {!loading && !error && counselors.length === 0 && (
            <div style={{ background: '#fff', border: '1px dashed #d1d5db', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No counselors have been assigned to you yet.
            </div>
          )}

          <div className="row g-4">
            {counselors.map((c) => (
              <div key={c.id} className="col-lg-6">
                <div style={{
                  background: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb',
                  padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', height: '100%',
                }}>
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #F47B20, #e8660f)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0,
                    }}>
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#111827' }}>{c.name}</h3>
                      {c.expertise && (
                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>{c.expertise}</p>
                      )}
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#374151' }}>
                        {c.email && (
                          <span><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px', color: '#9ca3af' }} />{c.email}</span>
                        )}
                        {c.phone && (
                          <span><FontAwesomeIcon icon={faPhone} style={{ marginRight: '8px', color: '#9ca3af' }} />{c.phone}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '14px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Meetings ({(c.meetings || []).length})
                    </h4>
                    {(c.meetings || []).length === 0 ? (
                      <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>No meetings scheduled with this counselor.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {c.meetings.map((m) => (
                          <div key={m.id} style={{
                            background: '#f9fafb', borderRadius: '10px', padding: '10px 12px',
                            border: '1px solid #f3f4f6',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: '#F47B20' }} />
                                {formatDate(m.meeting_date)}
                                {m.meeting_time && (
                                  <>
                                    {' · '}
                                    <FontAwesomeIcon icon={faClock} style={{ marginRight: '4px', color: '#9ca3af' }} />
                                    {formatTime(m.meeting_time)}
                                  </>
                                )}
                              </span>
                              {m.status && (
                                <span style={{
                                  fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                                  background: '#fff7ed', color: '#c2410c',
                                }}>
                                  {m.status}
                                </span>
                              )}
                            </div>
                            {m.notes && (
                              <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280' }}>{m.notes}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
